import { GQLPromoImageFragment } from '@theprintbar/graphql';
import { ImageSize } from '@theprintbar/shared';
import clsx from 'clsx';
import { HTMLAttributes } from 'react';
import { ButtonSize, OutlineButton, OutlineLinkButton } from '../Button';

export interface PromoImageContainerProps extends HTMLAttributes<HTMLDivElement> {
	promoImage?: GQLPromoImageFragment | null;
	imageClass?: string;
	lazyLoad?: boolean;
	promoPosition?: 'left' | 'right' | 'none';
	stickers?: {
		position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
		image: string | undefined;
		className?: string;
	}[];
	size?: ImageSize;
	//How wide the image renders at each breakpoint, so the browser can pick the right srcSet entry
	sizes?: string;
}

//Object.values on a numeric enum yields the names too, hence the typeof guard
const resizeWidths = (Object.values(ImageSize).filter(size => typeof size === 'number' && size > 0) as number[]).sort((a, b) => a - b);

export function PromoImageContainer({
	promoImage,
	imageClass,
	lazyLoad = true,
	promoPosition = 'right',
	className,
	stickers,
	size = ImageSize.Large,
	sizes = '(min-width: 1280px) 33vw, (min-width: 1024px) 50vw, 95vw',
	...divProps
}: PromoImageContainerProps) {
	if (!promoImage) return null;

	const { image, credit, creditUrl, altText, focalPointX, focalPointY } = promoImage;

	function sizedUrl(resize: number) {
		const url = new URL(image?.url || '');
		if (resize) url.searchParams.set('size', `${resize}`);
		return url.toString();
	}

	//The resizer scales the longest edge down to `size` and never upscales, so the width a
	//candidate actually comes back at depends on the original's dimensions and orientation
	function renderedWidth(resize: number) {
		const { width, height } = image ?? {};
		if (!width || !height) return resize;
		if (!resize) return width;
		const longestEdge = Math.min(resize, Math.max(width, height));
		return width >= height ? longestEdge : Math.round((longestEdge * width) / height);
	}

	//The target size plus up to 2 smaller ones. Original has no fixed size, so it gets the whole
	//ladder below the source's own size, with the untouched url as the largest entry
	function generateSrcSet(): string {
		const longestEdge = Math.max(image?.width || 0, image?.height || 0);
		if (!size && !longestEdge) return '';

		const smaller = resizeWidths.filter(w => (!size || w < size) && (!longestEdge || w < longestEdge));
		const candidates = [...(size ? smaller.slice(-2) : smaller), size];

		return candidates.map(w => `${sizedUrl(w)} ${renderedWidth(w)}w`).join(', ');
	}

	return (
		<div className={clsx('relative', className)} {...divProps}>
			<img
				loading={lazyLoad ? 'lazy' : 'eager'}
				{...{
					fetchpriority: lazyLoad ? 'auto' : 'high',
				}}
				decoding="async"
				src={sizedUrl(size)}
				srcSet={generateSrcSet()}
				alt={altText || ''}
				className={clsx('absolute object-cover h-full w-full', imageClass)}
				style={{ objectPosition: `${focalPointX || 50}% ${focalPointY || 50}%` }}
				sizes={sizes}
			/>
			{!!credit && promoPosition !== 'none' && (
				<div className={clsx('absolute bottom-5 font-normal text-xs', promoPosition === 'left' ? 'left-5' : 'right-5')}>
					{creditUrl ? (
						<OutlineLinkButton to={creditUrl} size={ButtonSize.XS} className="bg-white-pearl underline border-0 text-grey">
							<h6>{credit}</h6>
						</OutlineLinkButton>
					) : (
						<OutlineButton size={ButtonSize.XS} className="bg-white-pearl border-0 text-grey underline">
							<h6>{credit}</h6>
						</OutlineButton>
					)}
				</div>
			)}
			{stickers?.map(sticker => (
				<img
					loading="lazy"
					src={sticker.image}
					className={clsx(
						'hidden md:block absolute lg:h-32 lg:w-32 h-16 w-16 rounded-full',
						sticker.position === 'top-left' && 'left-8 -top-8 lg:-top-14 lg:left-16',
						sticker.position === 'top-right' && 'right-8  -top-8 lg:-top-14 lg:right-16',
						sticker.position === 'bottom-left' && 'left-8 -bottom-8 lg:-bottom-14 lg:left-16',
						sticker.position === 'bottom-right' && 'right-8 -bottom-8 lg:-bottom-14 lg:right-16 ',
						sticker.className,
					)}
				/>
			))}
		</div>
	);
}
